import React from "react";
import { Cookie } from "lucide-react";

import CookieSettingsLink from "../../ui/CookieSettingsLink";

const cookies = [
  {
    name: "_ga",
    purpose:
      "Tells returning browsers apart, so repeat visits are not counted as new visitors.",
    expires: "2 years",
  },
  {
    name: "_ga_<ID>",
    purpose:
      "Holds session state, such as when a visit started and whether it is still the same visit. There is one for each part of our web presence you visit: the main site, the documentation and the blog.",
    expires: "2 years",
  },
];

const facts = [
  {
    term: "Who receives the data",
    detail:
      "Google Ireland Limited and Google LLC, acting as our processor. This can involve a transfer outside the EEA. Google Analytics does not log or store visitors' IP addresses.",
  },
  {
    term: "Legal basis",
    detail:
      "Your consent, which you can withdraw at any time with no effect on how this site works.",
  },
  {
    term: "Changing your mind",
    detail:
      "Withdrawing is as easy as consenting. The cookies above are deleted when you decline. Your choice is remembered separately on www.telcofy.ai and docs.telcofy.ai, so you may be asked once on each. Use Cookie settings on the one you are visiting to change it.",
  },
  {
    term: "Other services",
    detail:
      "Videos are played by Vimeo with its do-not-track setting turned on, so the player does not set tracking cookies. Maps load their tiles from OpenStreetMap. Like any web server, both see your IP address when they send content to your browser. Fonts are served from this site.",
  },
];

const CookieNotice = () => (
  <section id="cookies" className="py-20 bg-paper">
    <div className="max-w-3xl mx-auto px-6">
      <div className="bg-paper-100 border border-paper-300 rounded-lg p-6 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          <Cookie size={15} className="text-ink-subtle" />
          <h2 className="font-display text-sm font-semibold text-ink">
            Analytics cookies · optional, off by default
          </h2>
        </div>

        <p className="text-sm text-ink-muted leading-relaxed">
          This notice covers www.telcofy.ai and docs.telcofy.ai, which hosts our
          documentation and blog. We use Google Analytics to understand which
          pages people read and how they arrive. It is the only non-essential
          technology on these sites.
          Nothing is loaded and no request reaches Google until you accept. If
          you decline, or simply ignore the banner, no cookies are set at all.
        </p>

        <div className="mt-6 divide-y divide-paper-300 border-y border-paper-300">
          {cookies.map(({ name, purpose, expires }) => (
            <div
              key={name}
              className="py-4 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1.5 sm:gap-6"
            >
              <div className="sm:flex-1">
                <p className="font-mono text-xs font-semibold text-brand-700">
                  {name}
                </p>
                <p className="text-xs text-ink-muted leading-relaxed mt-1">
                  {purpose}
                </p>
              </div>
              <p className="font-mono text-xs text-ink-subtle whitespace-nowrap">
                {expires}
              </p>
            </div>
          ))}
        </div>

        <dl className="mt-6 space-y-3 text-xs leading-relaxed">
          {facts.map(({ term, detail }) => (
            <div key={term}>
              <dt className="font-semibold text-ink inline">{term}. </dt>
              <dd className="text-ink-muted inline">{detail}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 pt-5 border-t border-paper-300">
          <CookieSettingsLink className="text-sm font-medium text-brand-600 hover:text-brand-700 underline underline-offset-2" />
        </div>
      </div>
    </div>
  </section>
);

export default CookieNotice;
